import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, CheckCircle, XCircle } from 'lucide-react';
import confetti from 'canvas-confetti';
import './PracticeWizardModal.css';

const WIZARD_STATES = {
  INTRO: 'INTRO',
  COUNTDOWN: 'COUNTDOWN',
  ACTIVE: 'ACTIVE',
  REFLECTION: 'REFLECTION'
};

/**
 * PracticeWizardModal - 4-step state machine for practice sessions
 */
export default function PracticeWizardModal({ task, isOpen, onClose, onComplete, t = (key, fallback) => fallback || key }) {
  const [currentStep, setCurrentStep] = useState(WIZARD_STATES.INTRO);
  const [countdown, setCountdown] = useState(3);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [currentInstructionIndex, setCurrentInstructionIndex] = useState(0);
  
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const confettiTimeoutRef = useRef(null);

  // Parse duration from task - memoized to prevent recalculation
  const totalDuration = useMemo(() => {
    if (!task) return 45; // Default 45 seconds
    
    const timeStr = task.practice_prompt_time || task.estimatedTime || '45s';
    const match = timeStr.match(/(\d+)/);
    return match ? parseInt(match[1]) : 45;
  }, [task?.practice_prompt_time, task?.estimatedTime]);

  const instructions = useMemo(() => {
    if (!task) return [];
    if (Array.isArray(task.mission)) return task.mission;
    if (task.mission) return [task.mission];
    if (task.practice_prompt_description) return [task.practice_prompt_description];
    return [];
  }, [task?.mission, task?.practice_prompt_description]);

  // Reset state when modal opens/closes or task changes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(WIZARD_STATES.INTRO);
      setCountdown(3);
      setTimeRemaining(totalDuration);
      setIsPaused(false);
      setCurrentInstructionIndex(0);
    } else {
      // Cleanup timers
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      if (confettiTimeoutRef.current) {
        clearTimeout(confettiTimeoutRef.current);
        confettiTimeoutRef.current = null;
      }
    }
  }, [isOpen, task?.id, totalDuration]);

  // Handle countdown step
  useEffect(() => {
    if (currentStep === WIZARD_STATES.COUNTDOWN) {
      // Clear any existing countdown timer first
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
            setCurrentStep(WIZARD_STATES.ACTIVE);
            return 3;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Cleanup when not in countdown step
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [currentStep]);

  // Handle active timer - CRITICAL FIX: removed timeRemaining from dependencies to prevent infinite loop
  useEffect(() => {
    if (currentStep === WIZARD_STATES.ACTIVE && !isPaused) {
      // Clear any existing timer first
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            setCurrentStep(WIZARD_STATES.REFLECTION);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentStep, isPaused]); // Removed timeRemaining - using functional update instead

  const handleStart = () => {
    setCurrentStep(WIZARD_STATES.COUNTDOWN);
  };

  const handlePause = () => {
    setIsPaused(prev => !prev);
  };

  const handleFinish = () => {
    setCurrentStep(WIZARD_STATES.REFLECTION);
  };

  // Calculate progress safely (prevent division by zero and ensure valid range)
  // IMPORTANT: This must be before early return to maintain consistent hook order
  const progress = useMemo(() => {
    if (!task || totalDuration <= 0) return 0;
    return Math.max(0, Math.min(100, ((totalDuration - timeRemaining) / totalDuration) * 100));
  }, [task, totalDuration, timeRemaining]);

  const currentInstruction = useMemo(() => {
    if (!Array.isArray(instructions) || instructions.length === 0) return '';
    // Cycle through instructions if there are multiple
    const index = currentInstructionIndex % instructions.length;
    return instructions[index] || '';
  }, [instructions, currentInstructionIndex]);

  const handleReflection = (success) => {
    if (success) {
      // Trigger confetti celebration!
      const count = 200;
      const defaults = {
        origin: { y: 0.7 },
        zIndex: 10001
      };

      function fire(particleRatio, opts) {
        confetti({
          ...defaults,
          ...opts,
          particleCount: Math.floor(count * particleRatio)
        });
      }

      // Left side burst (RTL - right side)
      fire(0.25, {
        spread: 26,
        startVelocity: 55,
        origin: { x: 0.8, y: 0.7 } // RTL: right side
      });

      // Center burst
      fire(0.2, {
        spread: 60,
        origin: { x: 0.5, y: 0.7 }
      });

      // Right side burst (RTL - left side)
      fire(0.25, {
        spread: 26,
        startVelocity: 55,
        origin: { x: 0.2, y: 0.7 } // RTL: left side
      });

      // Top burst
      fire(0.15, {
        spread: 100,
        scalar: 0.8,
        origin: { x: 0.5, y: 0.3 }
      });

      // Bottom burst
      fire(0.15, {
        spread: 100,
        scalar: 0.8,
        origin: { x: 0.5, y: 0.9 }
      });

      // Call onComplete after a short delay to let confetti start
      confettiTimeoutRef.current = setTimeout(() => {
        if (onComplete && task) {
          onComplete(task);
        }
        onClose();
      }, 300);
    } else {
      onClose();
    }
  };

  // Early return AFTER all hooks to maintain consistent hook order
  if (!isOpen || !task) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="practiceWizard__overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={currentStep === WIZARD_STATES.INTRO ? onClose : undefined}
          dir="rtl"
        >
          <motion.div
            className="practiceWizard__content"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            {/* Step 1: INTRO */}
            {currentStep === WIZARD_STATES.INTRO && (
              <div className="practiceWizard__step practiceWizard__step--intro">
                <button 
                  className="practiceWizard__closeBtn" 
                  onClick={onClose}
                  aria-label="סגור"
                >
                  <X size={24} aria-hidden="true" />
                </button>
                
                <h2 className="practiceWizard__title">{task.practice_prompt_title || task.title}</h2>
                
                {task.principle && (
                  <div className="practiceWizard__section">
                    <h3 className="practiceWizard__sectionTitle">העיקרון</h3>
                    <p className="practiceWizard__text">{task.principle.replace(/\*\*/g, '')}</p>
                  </div>
                )}
                
                {instructions && instructions.length > 0 && (
                  <div className="practiceWizard__section">
                    <h3 className="practiceWizard__sectionTitle">המשימה</h3>
                    {Array.isArray(instructions) ? (
                      <ul className="practiceWizard__list">
                        {instructions.map((step, idx) => (
                          <li key={idx} className="practiceWizard__listItem">{step.replace(/\*\*/g, '')}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="practiceWizard__text">{instructions.replace(/\*\*/g, '')}</p>
                    )}
                  </div>
                )}
                
                {task.executionNote && (
                  <div className="practiceWizard__section">
                    <h3 className="practiceWizard__sectionTitle">דגש לתרגול</h3>
                    <p className="practiceWizard__text">{task.executionNote.replace(/\*\*/g, '')}</p>
                  </div>
                )}
                
                <button 
                  className="practiceWizard__startBtn" 
                  onClick={handleStart}
                  aria-label="התחל תרגול"
                >
                  <Play size={24} aria-hidden="true" />
                  <span>התחל</span>
                </button>
              </div>
            )}

            {/* Step 2: COUNTDOWN */}
            {currentStep === WIZARD_STATES.COUNTDOWN && (
              <div className="practiceWizard__step practiceWizard__step--countdown" role="status" aria-live="polite">
                <motion.div
                  className="practiceWizard__countdownNumber"
                  key={countdown}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 1.5, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  aria-label={`${countdown} שניות להתחלה`}
                >
                  {countdown}
                </motion.div>
              </div>
            )}

            {/* Step 3: ACTIVE */}
            {currentStep === WIZARD_STATES.ACTIVE && (
              <div className="practiceWizard__step practiceWizard__step--active" role="region" aria-label="תרגול פעיל">
                <div className="practiceWizard__timer" role="timer" aria-valuenow={timeRemaining} aria-valuemin="0" aria-valuemax={totalDuration}>
                  <div className="practiceWizard__timerCircle">
                    <svg className="practiceWizard__timerSvg" viewBox="0 0 100 100" aria-hidden="true">
                      <circle
                        className="practiceWizard__timerBg"
                        cx="50"
                        cy="50"
                        r="45"
                      />
                      <motion.circle
                        className="practiceWizard__timerProgress"
                        cx="50"
                        cy="50"
                        r="45"
                        initial={{ pathLength: 1 }}
                        animate={{ pathLength: progress / 100 }}
                        transition={{ duration: 0.1 }}
                      />
                    </svg>
                    <div className="practiceWizard__timerText" aria-live="polite" aria-atomic="true">
                      {Math.max(0, timeRemaining)}s
                    </div>
                  </div>
                </div>
                
                {currentInstruction && (
                  <div className="practiceWizard__instruction" role="region" aria-label="הוראות תרגול">
                    <p className="practiceWizard__instructionText">{currentInstruction}</p>
                  </div>
                )}
                
                <div className="practiceWizard__activeActions">
                  <button
                    className="practiceWizard__actionBtn practiceWizard__actionBtn--pause"
                    onClick={handlePause}
                    aria-label={isPaused ? 'המשך תרגול' : 'השהה תרגול'}
                  >
                    {isPaused ? <Play size={20} aria-hidden="true" /> : <Pause size={20} aria-hidden="true" />}
                    <span>{isPaused ? 'המשך' : 'השהה'}</span>
                  </button>
                  
                  <button
                    className="practiceWizard__actionBtn practiceWizard__actionBtn--finish"
                    onClick={handleFinish}
                    aria-label="סיים תרגול"
                  >
                    <CheckCircle size={20} aria-hidden="true" />
                    <span>סיים</span>
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: REFLECTION */}
            {currentStep === WIZARD_STATES.REFLECTION && (
              <div className="practiceWizard__step practiceWizard__step--reflection" role="region" aria-label="רפלקציה">
                <h2 className="practiceWizard__reflectionTitle">מרגיש שהצלחת ליישם?</h2>
                
                <div className="practiceWizard__reflectionActions">
                  <button
                    className="practiceWizard__reflectionBtn practiceWizard__reflectionBtn--yes"
                    onClick={() => handleReflection(true)}
                    aria-label="כן, הצלחתי ליישם"
                  >
                    <CheckCircle size={24} aria-hidden="true" />
                    <span>הצלחתי</span>
                  </button>
                  
                  <button
                    className="practiceWizard__reflectionBtn practiceWizard__reflectionBtn--no"
                    onClick={() => handleReflection(false)}
                    aria-label="לא, לא הצלחתי"
                  >
                    <XCircle size={24} aria-hidden="true" />
                    <span>לא</span>
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

